import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import {
  ArrowRight,
  Award,
  Check,
  Copy,
  Download,
  FileQuestion,
  LayoutGrid,
  ListChecks,
  Mail,
  Paperclip,
  Pencil,
  Plus,
  Trash2,
  Upload,
  UserCog,
  X,
} from 'lucide-react'
import Button from '../../components/Button'
import Card from '../../components/Card'
import Checkbox from '../../components/Checkbox'
import ConfirmDialog from '../../components/ConfirmDialog'
import DataTable from '../../components/DataTable'
import PageHeader from '../../components/PageHeader'
import useGoBack from '../../components/useGoBack'
import RowActions from '../../components/RowActions'
import { useToast } from '../../components/toastContext'
import PageLoader from '../../components/PageLoader'
import { ErrorState, NotFoundState } from '../../components/States'
import ProgramCertificateDrawer from './ProgramCertificateDrawer'
import ProgramSurveyDrawer from './ProgramSurveyDrawer'
import ProgramGroupsDrawer from './ProgramGroupsDrawer'
import ExamFormDrawer from '../exams/ExamFormDrawer'
import { getExam, optionsForCourse, saveExam } from '../exams/examsService'
import CourseAttachmentsDrawer from './CourseAttachmentsDrawer'
import CourseExceptionsDrawer from './CourseExceptionsDrawer'
import CourseNotifyModal from './CourseNotifyModal'
import {
  addCourseNotification,
  cloneProgramAsCopy,
  deleteCourse,
  exportScormBundle,
  getCourseExamId,
  getProgram,
  getProgramsPage,
  importScormBundle,
  saveProgram,
  setProgramActive,
} from './programsService'
import { strings as S } from './strings'
import './ProgramDetailPage.css'
import { isEnglish, serverDetail, serverText } from '../../lib/i18n'
import { displayDate } from '../../lib/dates'

const D = S.detail

/**
 * تعديل البرنامج التدريبي — Views/ProgramsManagement/Index.cshtml.
 *
 * Header actions, the three summary cards (departments / survey / certificate) and
 * the programme's courses with their row actions and overflow menu. The delete
 * button on the programme itself is commented out in the legacy view, so it is not
 * rebuilt here.
 */
export default function ProgramDetailPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const goBack = useGoBack('/programs')
  const [program, setProgram] = useState(null)
  const [loadFailed, setLoadFailed] = useState(false)
  const [loading, setLoading] = useState(true)
  const [isActive, setIsActive] = useState(false)
  const toast = useToast()
  const [pendingDelete, setPendingDelete] = useState(null)
  const [confirmClone, setConfirmClone] = useState(false)
  const [reloadKey, setReloadKey] = useState(0)
  const [busy, setBusy] = useState(false)
  const importInput = useRef(null)

  const importPortable = async (event) => {
    const file = event.target.files?.[0]
    event.target.value = ''
    if (!file) return
    if (!file.name.toLowerCase().endsWith('.zip') || file.size > 201 * 1024 * 1024) {
      toast.error(D.scormImportInvalid)
      return
    }
    setBusy(true)
    try {
      await importScormBundle(Number(id), file)
      toast.success(D.scormImportSuccess)
      setReloadKey((key) => key + 1)
    } catch (error) {
      toast.error(isEnglish ? (serverDetail(error?.message) || D.scormImportFailed) : D.scormImportFailed)
    } finally {
      setBusy(false)
    }
  }
  const [notifying, setNotifying] = useState(null)
  const [attaching, setAttaching] = useState(null)
  const [excepting, setExcepting] = useState(null)
  /* `{ course, settings }` — the course whose exam is open, and the option lists
     positioned for it. Null when the drawer is closed. */
  const [examining, setExamining] = useState(null)
  const [editingGroups, setEditingGroups] = useState(false)
  const [editingCertificate, setEditingCertificate] = useState(false)
  const [editingSurvey, setEditingSurvey] = useState(false)
  /* The pickers the two panels choose from — the page payload already carries both. */
  const [lists, setLists] = useState({ groups: [], certificates: [] })

  /**
   * One place that turns an action's outcome into a message, and only then into a change
   * on screen. `onDone` runs on success alone — a refused save must not leave the page
   * showing something the server did not accept.
   */
  const act = async (call, { done, failed } = {}, onDone) => {
    setBusy(true)
    const outcome = await call
    setBusy(false)

    if (!outcome.ok) {
      toast.error(serverDetail(outcome.message) || failed || S.actionFailed)
      return false
    }

    toast.success(serverText(outcome.message) || done || S.messages.saved)
    onDone?.()
    setReloadKey((key) => key + 1)
    return true
  }

  /**
   * The legacy's `showExamForm(courseId)`.
   *
   * It asks the server whether the course already has an exam and branches on the answer —
   * `GetourseExamId` there, `GetCourseExamId` here, and the reply is the same: an id, or
   * zero. An id opens that exam with its rules; zero opens a blank one already bound to
   * this course. There is no list in between, because a course has one exam and the
   * question is only ever which one.
   *
   * Both branches fetch first: the rules are composed from topics the course covers and
   * difficulties those topics have questions at, and only the server knows either.
   */
  const openExam = useCallback(async (course) => {
    setBusy(true)
    try {
      const { examId } = await getCourseExamId(course.id)
      const settings = examId > 0 ? await getExam(examId) : await optionsForCourse(course.id)
      if (!settings) return toast.error(S.fail.exam)
      setExamining({ course, settings })
    } catch {
      toast.error(S.fail.exam)
    } finally {
      setBusy(false)
    }
  }, [toast])

  /* The groups and certificates to choose from.

     WITH THE PROGRAMME ID. `ViewProgramsManagementPageHandler` fills Groups, Certificates,
     Levels and DifficultyLevels only inside `if (request.ProgramId.HasValue)` — ask without
     one and all four come back empty, which is a picker with nothing in it. */
  useEffect(() => {
    let cancelled = false
    getProgramsPage(id)
      .then((page) => {
        if (!cancelled) setLists({ groups: page.groups, certificates: page.certificates })
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [id])

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    getProgram(id)
      .then((response) => {
        if (cancelled) return
        setProgram(response)
        setLoadFailed(false)
        setIsActive(!!response?.isActive)
        setLoading(false)
      })
      .catch(() => {
        if (cancelled) return
        setProgram(null)
        setLoadFailed(true)
        setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [id, reloadKey])

  const columns = useMemo(
    () => [
      {
        key: 'index',
        header: D.columns.index,
        width: '64px',
        sortable: false,
        searchable: false,
        render: (_row, index) => <span className="pd__index">{index + 1}</span>,
      },
      { key: 'name', header: D.columns.course, render: (row) => <bdi>{row.name}</bdi> },
      {
        key: 'content',
        header: D.columns.content,
        align: 'center',
        value: (row) => Number(row.contentType) === 1 ? 'SCORM' : 'MP4',
        render: (row) => <bdi>{Number(row.contentType) === 1 ? 'SCORM' : 'MP4'}</bdi>,
      },
      {
        key: 'hasExam',
        header: D.columns.exam,
        align: 'center',
        value: (row) => (row.hasExam ? 1 : 0),
        render: (row) =>
          row.hasExam ? (
            <Check className="pd__flag pd__flag--on" size={18} strokeWidth={2} />
          ) : (
            <X className="pd__flag" size={18} strokeWidth={2} />
          ),
      },
      {
        key: 'startDate',
        header: D.columns.startDate,
        render: (row) => (
          <span className="pd__date" dir="ltr">
            {displayDate(row.startDate)}
          </span>
        ),
      },
      {
        key: 'endDate',
        header: D.columns.endDate,
        render: (row) => (
          <span className="pd__date" dir="ltr">
            {displayDate(row.endDate)}
          </span>
        ),
      },
      {
        key: 'actions',
        header: <Pencil size={15} strokeWidth={1.75} />,
        align: 'center',
        width: '96px',
        sortable: false,
        searchable: false,
        /* ONE GLYPH AND A KEBAB. Six unlabelled icons per row is the wall `RowActions`
           exists to take down — nothing said which one to reach for, and the column had
           to be 212px wide to hold them. Editing is what this table is for, so it stays
           out front; the other five fold away and gain the visible Arabic labels the
           tooltip-only glyphs never had.

           THE PENCIL: it was held back because `SaveCourse` rewrites the exam from
           whatever it is posted and `GetProgramCoursesDetails` carries none of it, so an
           edit seeded from that alone reset the pass mark, the attempt count and the three
           switches without saying so. `GetCourseExamId` returns exactly those six — it is
           what the legacy loads when it opens a course — so `getCourseForEdit` reads both
           and the round trip is lossless. The exam's question rules are untouched by the
           update path, so they were never at risk. */
        render: (row) => (
          <RowActions
            inline={1}
            actions={[
              {
                id: 'edit',
                icon: Pencil,
                label: D.courseRow.edit,
                onClick: () => navigate(`/programs/${program.id}/courses/${row.id}/edit`),
              },
              /* THE EXAM, where the legacy keeps it: a per-course action on the courses
                 table, not a screen of its own. `hasExam` in the row already says whether
                 there is one, so this opens the right thing without asking twice. */
              {
                id: 'exam',
                icon: FileQuestion,
                label: row.hasExam ? D.courseRow.editExam : D.courseRow.addExam,
                /* It authors an exam — an edit, in the colour of every other edit. */
                tone: 'brand',
                disabled: busy,
                onClick: () => openExam(row),
              },
              ...(Number(row.contentType) === 1 ? [{
                id: 'export-scorm',
                icon: Download,
                label: D.courseRow.exportScorm,
                disabled: busy,
                onClick: async () => {
                  setBusy(true)
                  try { await exportScormBundle(row.id) }
                  catch (error) { toast.error(isEnglish ? (serverDetail(error?.message) || D.scormExportFailed) : D.scormExportFailed) }
                  finally { setBusy(false) }
                },
              }] : []),
              /* The attachments drawer — four endpoints that had been in the service since
                 the slice was wired with nothing in front of them. */
              {
                id: 'attachments',
                icon: Paperclip,
                label: D.courseRow.attachments,
                tone: 'primary',
                onClick: () => setAttaching(row),
              },
              {
                id: 'exceptions',
                icon: UserCog,
                label: D.courseRow.exceptionsList,
                tone: 'violet',
                onClick: () => setExcepting(row),
              },
              {
                id: 'notify',
                icon: Mail,
                label: isActive ? D.courseRow.notify : D.courseRow.notifyDisabled,
                tone: isActive ? 'success' : 'primary',
                disabled: !isActive,
                onClick: () => setNotifying(row),
              },
              {
                id: 'delete',
                icon: Trash2,
                label: D.courseRow.delete,
                danger: true,
                onClick: () => setPendingDelete(row),
              },
            ]}
          />
        ),
      },
    ],
    [isActive, busy, openExam, navigate, program?.id, toast],
  )

  if (loading) {
    return (
      <>
        <PageHeader title={D.title} />
        <PageLoader />
      </>
    )
  }

  /* No programme with this id is a wrong address, not a failure (Taiga #1547). */
  if (!program && !loadFailed) {
    return (
      <>
        <PageHeader title={D.title} />
        <NotFoundState home="/programs" />
      </>
    )
  }

  if (!program) {
    return (
      <>
        <PageHeader title={D.title} />
        <Card>
          {/* It used to repeat the page title back at you and stop there: no
              statement that anything failed, and no way on from a dead end. */}
          <ErrorState
            title={D.errorHeading}
            action={
              <Button as={Link} variant="ghost" to="/programs">
                <ArrowRight className="ltr-flip" size={16} strokeWidth={1.75} />
                {D.back}
              </Button>
            }
          />
        </Card>
      </>
    )
  }

  const cards = [
    {
      key: 'groups',
      icon: LayoutGrid,
      count: program.groupsCount,
      done: program.groupsCount > 0,
      hint: D.setUp,
      onOpen: () => setEditingGroups(true),
    },
    {
      key: 'survey',
      icon: ListChecks,
      count: null,
      /* The legacy's own test: a survey nobody can answer is not a survey this programme
         has, so the tick needs BOTH — `HasAnySurveyQuestion && IsSurveyEnabled`. */
      done: program.hasSurvey && program.isSurveyEnabled,
      hint: D.setUp,
      /* IN PLACE, as `showSurveyModalForType('program', id)` does. It used to navigate to
         the surveys screen, which is not what the legacy does and drops the programme on
         the way there. */
      onOpen: () => setEditingSurvey(true),
    },
    {
      key: 'certificate',
      icon: Award,
      count: null,
      done: !!program.certificateId,
      hint: D.setUp,
      onOpen: () => setEditingCertificate(true),
    },
  ]

  return (
    <>
      <PageHeader
        /* Was a ghost button among the actions at the far end; it is the same
           arrow on every details page now, and it returns to the list as it was
           left rather than to its first page. */
        onBack={goBack}
        backLabel={D.back}
        title={program.name}
        subtitle={program.description || undefined}
      />

      {/* Two columns: the courses *are* the page, so they start at the top instead
          of below a full-width band of summary cards. The cards and the
          programme-level actions move to a side column that stays with them. */}
      <div className="pd__layout">
        <div className="pd__main">
          <DataTable
            title={D.coursesTitle}
            columns={columns}
            rows={program.courses}
            getRowId={(row) => row.id}
            initialSort={{ key: 'name', dir: 'asc' }}
            toolbar={
              <>
                <input ref={importInput} type="file" accept=".zip,application/zip" hidden onChange={importPortable} />
                <Button variant="ghost" disabled={busy || isActive} title={isActive ? D.scormImportInactive : D.scormImportHint}
                  onClick={() => importInput.current?.click()}>
                  <Upload size={16} strokeWidth={2} />{D.importScorm}
                </Button>
                <Button onClick={() => navigate(`/programs/${program.id}/courses/new`)}>
                  <Plus size={16} strokeWidth={2} />{D.newCourse}
                </Button>
              </>
            }
          />
        </div>

        <aside className="pd__side">
          <div className="pd__panel">
            {/* This was local state and nothing else: ticking it changed the box and
                the programme stayed exactly as it was. It POSTS now, and only moves
                once the server has taken it — a refused change must not leave the box
                showing something the database does not agree with. */}
            <Checkbox
              id="program-active"
              checked={isActive}
              disabled={busy}
              onChange={(next) =>
                act(
                  setProgramActive(program.id, next),
                  next
                    ? { done: S.messages.programActivated, failed: S.fail.activate }
                    : { done: S.messages.programDisabled, failed: S.fail.disable },
                  () => setIsActive(next),
                )
              }
            >
              <bdi>{D.active}</bdi>
            </Checkbox>

            <Button variant="ghost" onClick={() => navigate(`/programs/${program.id}/edit`)}>
              <Pencil size={16} strokeWidth={1.75} />
              {D.edit}
            </Button>

            {/* `إجبار المستخدمين على إعادة البرنامج` is NOT here. The legacy posts it to
                `ProgramsManagement/ResetProgramForUsers`, which this project's slice does
                not have — twenty-two routes and none of them resets a programme for
                anyone. A button that cannot do what it says is worse than its absence;
                COSMETIC_UI.md carries it with what it would need. */}

            {/* Asks first, as the list does (Taiga #1531). */}
            <Button variant="ghost" disabled={busy} onClick={() => setConfirmClone(true)}>
              <Copy size={16} strokeWidth={1.75} />
              {D.clone}
            </Button>
          </div>

          {/* THE WAY IN, not just a state. Each of these said whether a programme had
              departments or a certificate and offered no way to give it either —
              `SaveProgramGroups` and `SaveProgramCertificate` had been in the service since
              the slice was wired with nothing in front of them. The survey has no endpoint
              in this slice, so its card opens the surveys screen, which does. */}
          <ul className="pd__cards">
            {cards.map(({ key, icon: Icon, count, done, onOpen, hint }) => (
              <li key={key}>
                <button type="button" className="pd__card pd__card--action" onClick={onOpen} title={hint}>
                  <span className="pd__card-icon">
                    <Icon size={20} strokeWidth={1.75} />
                  </span>
                  <span className="pd__card-label">
                    <bdi>
                      {D.cards[key]}
                      {count != null && ` (${count})`}
                    </bdi>
                  </span>
                  <span className={`pd__card-state${done ? ' pd__card-state--done' : ''}`}>
                    {done ? <Check size={15} strokeWidth={2.5} /> : <X size={15} strokeWidth={2.5} />}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </aside>
      </div>

      <ProgramSurveyDrawer
        open={editingSurvey}
        program={program}
        groups={lists.groups}
        onClose={() => setEditingSurvey(false)}
        onSaved={(outcome) => {
          if (!outcome.ok) return toast.error(serverDetail(outcome.message) || S.fail.save)
          setEditingSurvey(false)
          toast.success(serverText(outcome.message) || S.messages.saved)
          /* The card reads the survey's state, so the programme has to be re-read. */
          setReloadKey((key) => key + 1)
        }}
      />

      <ProgramGroupsDrawer
        open={editingGroups}
        program={program}
        groups={lists.groups}
        saving={busy}
        onClose={() => setEditingGroups(false)}
        onSave={async (values) => {
          const ok = await act(saveProgram({ ...program, ...values }, { groups: true }), {
            done: S.messages.groupsSaved,
            failed: S.fail.groups,
          })
          if (ok) setEditingGroups(false)
        }}
      />

      <ProgramCertificateDrawer
        open={editingCertificate}
        program={program}
        certificates={lists.certificates}
        saving={busy}
        onClose={() => setEditingCertificate(false)}
        onSave={async (values) => {
          const ok = await act(saveProgram({ ...program, ...values }, { certificate: true }), {
            done: S.messages.certificateSaved,
            failed: S.fail.certificate,
          })
          if (ok) setEditingCertificate(false)
        }}
      />

      <ExamFormDrawer
        open={!!examining}
        settings={examining?.settings}
        course={examining?.course}
        onClose={() => setExamining(null)}
        onSave={async (values) => {
          const outcome = await saveExam({ ...values, courseId: examining.course.id })
          if (!outcome.ok) return toast.error(serverDetail(outcome.message) || S.fail.saveExam)

          setExamining(null)
          toast.success(serverText(outcome.message) || S.messages.saved)
          /* The courses table carries a `hasExam` tick, so it has to be re-read. */
          setReloadKey((key) => key + 1)
        }}
      />

      <CourseAttachmentsDrawer
        open={!!attaching}
        course={attaching}
        onClose={() => setAttaching(null)}
        /* The drawer stays OPEN and reloads its own list — an attachment is usually one of
           several, and closing after each one would mean reopening for the next. */
        onOutcome={(message, failed) =>
          failed
            ? toast.error(message || S.fail.attachment)
            : toast.success(serverText(message) || S.messages.saved)
        }
      />

      <CourseExceptionsDrawer
        open={!!excepting}
        programId={program?.id ?? Number(id)}
        course={excepting}
        onClose={() => setExcepting(null)}
        onOutcome={(message, failed) =>
          failed
            ? toast.error(message || S.fail.exception)
            : toast.success(serverText(message) || S.messages.saved)
        }
      />

      <CourseNotifyModal
        open={!!notifying}
        course={notifying}
        saving={busy}
        onClose={() => setNotifying(null)}
        onSubmit={(values) =>
          act(
            addCourseNotification(notifying.id, values),
            { done: S.messages.notificationSaved, failed: S.fail.notification },
            () => setNotifying(null),
          )
        }
      />

      <ConfirmDialog
        open={!!pendingDelete}
        message={D.deleteCourseConfirm}
        confirmLabel={D.courseRow.delete}
        cancelLabel={S.close}
        /* Deleting a course closed the dialog and said so; the course stayed. */
        onConfirm={async () => {
          const outcome = await deleteCourse(id, pendingDelete.id)
          if (!outcome.ok) return toast.error(serverDetail(outcome.message) || S.fail.deleteCourse)
          setPendingDelete(null)
          toast.success(serverText(outcome.message) || S.deleted)
          setReloadKey((key) => key + 1)
        }}
        onCancel={() => setPendingDelete(null)}
      />

      <ConfirmDialog
        open={confirmClone}
        tone="primary"
        title={S.cloneTitle}
        message={S.cloneConfirm}
        confirmLabel={D.clone}
        cancelLabel={S.close}
        onConfirm={async () => {
          setConfirmClone(false)
          await act(
            /* The two names per language, not the display name — that is only one of
               them, and the other column would get a copy of it. */
            cloneProgramAsCopy(program),
            { done: S.messages.programCopied, failed: S.fail.copy },
            /* The copy is a different programme; staying on this one's page after
               making it would leave no sign that anything happened. */
            () => navigate('/programs'),
          )
        }}
        onCancel={() => setConfirmClone(false)}
      />
    </>
  )
}
